import { StylesheetType } from '@common/flare-data/enums/stylesheet-type.enum';
import { CssAttributeName, CssSchemaPlugin } from '@common/flare-data/plugins/css-schema.plugin';
import { FlareDataSchema } from '@common/flare-data/schemas/flare-data-schema';
import { AstChange, CssSerializer } from '@common/flare-data/util/css-serializer';
import { TextSchemaPlugin } from '@common/flare/schema/text-schema.plugin';
import { SchemaPlugin } from '@common/prosemirror/model/schema-plugin';
import { AnyNode, Root } from 'postcss';
import { ProseMirrorNode } from 'prosemirror-model';

/** Prosemirror document schema for CSS. */
export class CssSchema extends FlareDataSchema {

  private readonly _serializer = new CssSerializer();

  get name(): string { return 'css'; }
  get version(): string { return '0.1.0'; }
  get serializer(): CssSerializer { return this._serializer; }
  get validateDoc(): (root: Root) => void { return (root: Root) => { } }
  /** Indicates whether the document code can be empty. */
  get codeCanBeEmpty(): boolean { return true; }

  constructor(createGuid?: () => string) {
    const schemaPlugins: SchemaPlugin[] = [
      new CssSchemaPlugin(),
      new TextSchemaPlugin(), // add 'text' node due to prosemirror requirements
    ];
    super(schemaPlugins, createGuid);
  }

  /** Converts the special type of css code to a prosemirror document. */
  cssToDoc(code: string, type: StylesheetType): ProseMirrorNode {
    const doc = super.codeToDoc(code);
    // check whether the stylesheet type is correct if not throw an error
    this.validateStylesheetType(doc, type);
    return doc;
  }

  /** Converts the prosemirror document node to the AST root node. */
  docToAst(doc: ProseMirrorNode): Root {
    return this.serializer.nodeToAst(doc.firstChild) as Root;
  }

  /** Synchronizes the prosemirror node state with AST node state and writes changes that were made.
   * @param node The prosemirror node which state to apply to AST node.
   * @param ast The AST node where to apply prosemirror node state.
   * @param change The changes accumulator.
  */
  applyNodeToAst(node: ProseMirrorNode, ast: AnyNode, change: AstChange): void {
    this.serializer.applyNodeToAst(node, ast, change);
  }

  /** Validates whether the stylesheet type of the document is correct. */
  validateStylesheetType(doc: ProseMirrorNode, expectedType: StylesheetType): void {
    // check whether the stylesheet type is correct if not throw an error
    const type = doc.firstChild.attrs[CssAttributeName.type];
    if (type !== expectedType) {
      switch (expectedType) {
        case StylesheetType.Branding:
          throw new Error('Stylesheet must contain branding declaration comment. To fix add "/* MadCap-Branding */" to the top of the file.');
        default:
          if (type === StylesheetType.Branding) {
            throw new Error('The stylesheet cannot be changed into a branding stylesheet. To fix remove "/* MadCap-Branding */" declaration comment from the top of the file.');
          }
      }
    }
  }

}
